import { useCallback, useMemo } from 'react'
import { type Address, ethAddress, formatEther, isAddressEqual } from 'viem'
import { isPricesApiChain } from '@curvefi/prices-api'
import { getUsdPrice } from '@curvefi/prices-api/usd-price'
import { getLib } from '@evm-ui/features/connect-wallet'
import type { LibKey } from '@evm-ui/features/connect-wallet/lib/types'
import { getWagmiConfig } from '@evm-ui/features/connect-wallet/lib/wagmi/wagmi-config'
import type { ChainParams, TokenParams, TokenQuery } from '@evm-ui/queries/query-types'
import { tokenValidationGroup } from '@evm-ui/queries/validation/token-validation'
import { BlockchainIds, REUSD_ADDRESS, SREUSD_ADDRESS } from '@evm-ui/utils'
import { FetchError } from '@primitives/fetch.utils'
import { Chain } from '@primitives/network.utils'
import { type QueriesResults } from '@tanstack/react-query'
import { combineQueriesToObject } from '@ui/features/queries/combine'
import { NoRetryError, queryFactory } from '@ui/features/queries/factory'
import { useQueries } from '@ui/features/queries/query-hooks'
import type { QueryData } from '@ui/features/queries/util'
import { createValidationSuite } from '@ui/lib/validation/lib'
import { readContract } from '@wagmi/core'

const getTestTokenPrice = async (chainId: number, tokenAddress: Address): Promise<number | null> => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- Existing violation before enabling this rule.
  if (chainId === Chain.Optimism) {
    if (isAddressEqual(tokenAddress, '0x79cc1c5c0171ff25ef391055e529a56a12bf3d39'))
      return await fetchUsdRate(chainId, ethAddress)
    if (isAddressEqual(tokenAddress, '0xe8e9cd957dc2b5a32ea822a3799f65940cf51f19')) return 1
  }
  return null
}

/** Try Curve/Llama API (returns null on failure) */
const fetchFromCurveLib = async (libName: LibKey, chainId: number, tokenAddress: string): Promise<number | null> => {
  const lib = getLib(libName)
  const rate = lib?.chainId === chainId && (await lib.getUsdRate(tokenAddress)) // failures by lib return a 0 value
  return rate || null
}

/** Try prices API (returns null on failure) */
const fetchFromPricesApi = async (chainId: number, tokenAddress: string) => {
  const blockchainId = BlockchainIds[chainId]
  if (!blockchainId || !isPricesApiChain(blockchainId)) return null

  try {
    const { usdPrice } = await getUsdPrice(blockchainId, tokenAddress as Address)
    return usdPrice || null
  } catch (error) {
    // Only swallow 404 errors (token not found), rethrow other issues for retries
    if (error instanceof FetchError && error.status === 404) {
      return null
    }
    throw error
  }
}

const sreusdAbi = [
  {
    inputs: [],
    name: 'pricePerShare',
    outputs: [{ internalType: 'uint256', name: '_pricePerShare', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const

/** Fallback for sreusd - derives price from reusd and the sreusd price per share */
const fetchFallbackSreUsd = async (chainId: number, tokenAddress: string): Promise<number | null> => {
  if (!isAddressEqual(tokenAddress as Address, SREUSD_ADDRESS)) return null

  // Fetch reusd price using the normal token rate fetcher, hopefully that won't fail too!
  const reusdPrice = await fetchTokenUsdRate({ chainId, tokenAddress: REUSD_ADDRESS })

  // Calculate sreusd price based on reusd and exchange rate
  const config = getWagmiConfig()
  if (!config) return null

  const exchangeRate = await readContract(config, {
    address: SREUSD_ADDRESS,
    abi: sreusdAbi,
    functionName: 'pricePerShare',
    chainId,
  })

  return reusdPrice * +formatEther(exchangeRate)
}

/** Main fetcher that tries all sources in order */
const fetchUsdRate = async (chainId: number, tokenAddress: string) => {
  const rate =
    (await getTestTokenPrice(chainId, tokenAddress as Address)) ??
    (await fetchFromCurveLib('curveApi', chainId, tokenAddress)) ??
    (await fetchFromCurveLib('llamaApi', chainId, tokenAddress)) ??
    (await fetchFallbackSreUsd(chainId, tokenAddress)) ??
    (await fetchFromPricesApi(chainId, tokenAddress))

  // Don't bother with retries if we've exchausted all options (and each option didn't unexpectedly fail)
  if (rate === null) {
    throw new NoRetryError(`Failed to fetch USD rate for token ${tokenAddress} on chain ${chainId}`)
  }

  return rate
}

/**
 * Hook to fetch the USD rate for a specific token on a specific blockchain.
 * First attempts to use Curve/Llama APIs when available.
 * Falls back to the prices API if no matching library is available.
 */
export const {
  setQueryData: setTokenUsdRateQueryData,
  useQuery: useTokenUsdRate,
  fetchQuery: fetchTokenUsdRate,
  getQueryOptions: getTokenUsdRateQueryOptions,
  queryKey: getTokenUsdRateKey,
} = queryFactory({
  queryKey: ({ chainId, tokenAddress }: TokenParams) => ({ name: 'usdRate', chainId, tokenAddress }) as const,
  queryFn: async ({ chainId, tokenAddress }: TokenQuery) => await fetchUsdRate(chainId, tokenAddress),
  validationSuite: createValidationSuite(({ chainId, tokenAddress }: TokenParams) =>
    tokenValidationGroup({ chainId, tokenAddress }),
  ),
  disableLog: true, // too much noise in the logs
  category: 'global.tokenRate',
})

type UseTokenOptions = ReturnType<typeof getTokenUsdRateQueryOptions>

/** Hook to fetch USD rates for multiple tokens on a specific blockchain. */
export const useTokenUsdRates = (
  { chainId, tokenAddresses = [] }: ChainParams & { tokenAddresses?: string[] },
  enabled = true,
) => {
  const uniqueAddresses = useMemo(() => Array.from(new Set(tokenAddresses)), [tokenAddresses])
  return useQueries({
    queries: useMemo(
      (): UseTokenOptions[] =>
        uniqueAddresses.map(tokenAddress => ({ ...getTokenUsdRateQueryOptions({ chainId, tokenAddress }), enabled })),
      [chainId, uniqueAddresses, enabled],
    ),
    combine: useCallback(
      (results: QueriesResults<UseTokenOptions[]>) => combineQueriesToObject(results, uniqueAddresses),
      [uniqueAddresses],
    ),
  })
}

export type TokenUsdRate = QueryData<typeof useTokenUsdRate>
export type TokenUsdRates = ReturnType<typeof useTokenUsdRates>
